/**
 * Nicht-HttpOnly-Marker-Cookie, das die Route-Guards (_app, _setup, _auth,
 * index) synchron lesen, um zu entscheiden, ob eine Backend-Session existiert.
 * Das Laravel-Session-Cookie selbst ist HttpOnly und für JS unsichtbar, dieser
 * Marker ist also das Einzige, worauf der Router schauen kann.
 *
 * Der BFF setzt ihn für die Flows, die er selbst abschließt (OAuth-Callback,
 * Register-Verify, /user/profile); jeder clientseitige Flow, der eine Session
 * begründet oder beendet, muss ihn ebenfalls nachziehen — sonst werfen die
 * Guards einen angemeldeten Nutzer auf /login. `ensureSessionMarker` in
 * ./session-probe repariert einen trotzdem verlorenen Marker.
 *
 * Die Attribute müssen zu denen des BFF passen (server/lib/auth-cookie.ts),
 * sonst stuft der eine Schreiber das Cookie des anderen still herab.
 */
const COOKIE_NAME = "hasUser";

/**
 * Ein Jahr. Der Marker muss einen Browser-Neustart überleben, weil die
 * Laravel-Session das auch tut — sonst steht der Kunde nach dem Neustart vor
 * dem Login-Fenster, obwohl seine Session noch lebt.
 */
const MARKER_MAX_AGE_SECONDS = 31536000;

function readCookie(name: string): string | null {
	const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
	if (!match) {
		return null;
	}

	try {
		return decodeURIComponent(match[1]);
	} catch {
		// Ein manipulierter Wert mit kaputtem Prozent-Escape (z. B. "%") wirft
		// URIError. Das darf ein beforeLoad nicht sprengen: Zustand gilt dann als
		// unbekannt.
		return null;
	}
}

/** `; secure` nur über HTTPS — sonst verwirft der Browser das Cookie. */
function secureAttribute(): string {
	return window.location.protocol === "https:" ? "; secure" : "";
}

/**
 * Marker auf "es gibt eine Session" setzen. Persistent, damit ein
 * Browser-Neustart ihn nicht verliert, während die Backend-Session weiterlebt.
 */
export function markSessionActive(): void {
	document.cookie = `${COOKIE_NAME}=true; path=/; samesite=lax; max-age=${MARKER_MAX_AGE_SECONDS}${secureAttribute()}`;
}

/**
 * Marker auf "geprüft, keine Session" setzen — bewusst nicht löschen. Ein
 * gelöschtes Cookie hieße "unbekannt", und nach einem fehlgeschlagenen Logout
 * (502, Netzabbruch) würde die Session-Probe den Nutzer wieder einloggen,
 * obwohl er sich gerade abgemeldet hat. Auf geteilten Geräten ist das ein
 * echtes Problem.
 *
 * Standardfall ist ein Session-Cookie: `false` ist der kurzlebigere Zustand und
 * soll sich spätestens beim nächsten Browserstart wieder von selbst klären.
 *
 * `persist: true` gehört genau dorthin, wo der Nutzer die Abmeldung ausdrücklich
 * wollte, sie aber serverseitig nicht bestätigt ist. Ein Abmeldewunsch ist eine
 * starke Aussage und darf nicht von der Selbstheilung der Probe überstimmt
 * werden — sonst findet der nächste Browserstart die noch lebende Session und
 * loggt wieder ein.
 */
export function markSessionEnded(options?: { persist?: boolean }): void {
	const maxAge = options?.persist ? `; max-age=${MARKER_MAX_AGE_SECONDS}` : "";
	document.cookie = `${COOKIE_NAME}=false; path=/; samesite=lax${maxAge}${secureAttribute()}`;
}

/** Marker sagt: es gibt eine Backend-Session. */
export function hasUserCookie(): boolean {
	return readCookie(COOKIE_NAME) === "true";
}

/**
 * Marker ist gesetzt — egal ob true oder false. `false` bedeutet "geprüft,
 * keine Session"; fehlt das Cookie ganz, ist der Zustand schlicht unbekannt und
 * muss erst geklärt werden.
 */
export function isSessionMarkerKnown(): boolean {
	return readCookie(COOKIE_NAME) !== null;
}
